//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { AddressLike, Contract, EventLog, Signer } from 'ethers'

import { ActivityEnum, ChainError, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolFactoryDynamicAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { navActions } from '@react/redux/navSlice'
import { logOT } from '@shared/loggingShared'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'

import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'
import { IPoolConfigurableSettingsDynamicStruct } from '@typechain-types/contracts/PoolDynamic'

export const createPoolDynamicTx =
  (props: {
    poolConfigSettings: string[]
    name: string
    tokenSymbol: string
    liquidityAssetAddress: EthAddr
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolConfigSettings, name, tokenSymbol, liquidityAssetAddress } = props
    const activity = ActivityEnum.POOL_CREATE
    try {
      logOT.info(activity, poolConfigSettings)

      const { webSocket, userConnection } = getState().chainState

      const { chainConfigurationName, perimeterAddresses } = getState().chainState.chainConfiguration
      otAssert(perimeterAddresses, 'perimeterAddresses is not defined')
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection

      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      otAssert(!!signer, 'signer is not defined')

      const createPoolReturn = await createPoolDynamic(
        perimeterAddresses,
        webSocket,
        signer as Signer,
        poolConfigSettings,
        name,
        tokenSymbol,
        liquidityAssetAddress,
        chainConfigurationName
      )
      logOT.info('createPoolReturn', createPoolReturn)
      const poolAddr: EthAddr = createPoolReturn.poolAddr
      dispatch(poolActions.creatPoolSuccess({ poolAddr }))

      dispatch(navActions.navigateTo({ url: `/pools/${poolAddr}` }))
    } catch (e: any) {
      dispatch(poolActions.creatPoolFail())
      await dispatchError(dispatch, e, activity, 1047)
    }
  }

export const createPoolDynamic = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolConfigSettings: string[],
  name: string,
  tokenSymbol: string,
  liquidityAssetAddress: EthAddr,
  chainConfigurationName: string
): Promise<{
  poolAddr: EthAddr
}> => {
  try {
    console.log('start createPoolDynamic')

    logOT.info(
      'createPool poolConfigSettings',
      poolConfigSettings,
      perimeterAddresses.PoolFactoryDynamic,
      !!poolAdminSigner,
      liquidityAssetAddress
    )
    console.log('Contract', perimeterAddresses.PoolFactoryDynamic, PoolFactoryDynamicAbi, poolAdminSigner)
    const poolFactoryDynamicContract = new Contract(
      perimeterAddresses.PoolFactoryDynamic,
      PoolFactoryDynamicAbi,
      poolAdminSigner
    )

    otAssert(poolConfigSettings.length === 7)

    otAssert(poolConfigSettings[0].startsWith('0x')) //borrowerManager
    otAssert(poolConfigSettings[1].startsWith('0x')) //borrowerWalletAddress
    otAssert(poolConfigSettings[2].startsWith('0x')) //borrowerWalletAddress
    otAssert(Number(poolConfigSettings[6]) > 0) //exchangeRateType

    const poolSettings: IPoolConfigurableSettingsDynamicStruct = {
      borrowerManager: poolConfigSettings[0],
      borrowerWalletAddr: poolConfigSettings[1],
      feeCollectorAddress: poolConfigSettings[2],
      closeOfDepositTime: poolConfigSettings[3],
      closeOfWithdrawTime: poolConfigSettings[4],
      transferOutDays: poolConfigSettings[5],
      exchangeRateType: poolConfigSettings[6],
    }

    console.log('Create Pool Dyno', liquidityAssetAddress, poolConfigSettings, name, tokenSymbol)

    const uuid = txUUID()
    webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_CREATE, ETH_ADDR_ZERO, uuid, chainConfigurationName))
    logOT.debug('Create Pool Params', liquidityAssetAddress, poolSettings, name, tokenSymbol)
    const createdPoolTx = await poolFactoryDynamicContract.createPool(
      liquidityAssetAddress as AddressLike,
      poolSettings,
      name,
      tokenSymbol
    )

    logOT.info('PerimeterAddresses.createdPoolTx,')

    const receipt = await createdPoolTx.wait(1)
    const poolCreatedEvent = receipt?.logs?.find((event: EventLog) => event.eventName === 'PoolCreated')

    logOT.debug(' poolCreatedEvent ', poolCreatedEvent)
    if (!poolCreatedEvent) {
      logOT.error('poolCreatedEvent not found', receipt)
    }

    const poolAddr = poolCreatedEvent.args.addr as EthAddr
    logOT.info('PerimeterAddresses.poolAddr,', poolAddr)

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_CREATE,
        poolAddr,
        uuid,
        createdPoolTx.hash as TxHash,
        chainConfigurationName
      )
    )

    logOT.debug('createPool Return', poolAddr)
    return { poolAddr }
  } catch (e: unknown) {
    const error = e as ChainError
    logOT.error('createPool error', error.reason ?? error)
    logOT.error(e)
    throw e
  }
}
